import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { IconButton, Tooltip } from "@mui/material";
import { useCallback } from "react";

import {
  $findDiscoverContext,
  $selectedProductContext,
  INSERT_PRODUCT_BLOCK_COMMAND,
  INSERT_PRODUCT_CONTEXT_COMMAND,
} from "./ProductBlockPlugin";
import { useDefaultProductContextModel } from "./useDefaultProductContextModel";
import type { EditorToolbarItemProps } from "../../../editor/extensionContract";

// Same glyph the email editor's Product Grid block uses, so a product means the
// same thing across both composers.
import { PRODUCT_GRID_MODELS } from "@/features/EmailEditor/plugins/productGridConstants";
import { OrionProductGridIcon } from "@/orion/OrionIcons/OrionProductGridIcon";

const FIRST_STATIC_MODEL =
  PRODUCT_GRID_MODELS.find((m) => m.value !== "discover")?.value ?? "discover";

/**
 * Outside a context, starts one on the tile's model — products always live in
 * a context, so the model is always explicit. Inside one, appends the next
 * product. No cap: `max_products` is a request ceiling the backend sums per
 * model, and each block's guard drops itself when its item isn't sourced.
 */
export function ProductBlockButton({ disabled }: EditorToolbarItemProps) {
  const [editor] = useLexicalComposerContext();
  const defaultModel = useDefaultProductContextModel();

  const handleClick = useCallback(() => {
    editor.update(() => {
      if ($selectedProductContext()) {
        editor.dispatchCommand(INSERT_PRODUCT_BLOCK_COMMAND, undefined);
        return;
      }
      // One discover group per message: with it in place, a new section
      // outside it starts on another model (switchable from its pill).
      const model =
        defaultModel === "discover" && $findDiscoverContext() !== null
          ? FIRST_STATIC_MODEL
          : defaultModel;
      editor.dispatchCommand(INSERT_PRODUCT_CONTEXT_COMMAND, model);
    });
  }, [editor, defaultModel]);

  return (
    <Tooltip title="Add product">
      <span>
        <IconButton
          size="small"
          aria-label="Add product block"
          disabled={disabled}
          onClick={handleClick}
          sx={{ color: "text.primary" }}
        >
          {/* 16px to match the media and emoji buttons beside it. */}
          <OrionProductGridIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}
